import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL } from '../config/api';
import OrderMessages from '../components/OrderMessages';
import ReviewModal from '../components/ReviewModal';
import './Orders.css';

const Orders = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [messagesOrder, setMessagesOrder] = useState(null);
  const [reviewOrder, setReviewOrder] = useState(null);
  const [canReviewMap, setCanReviewMap] = useState({});
  const [reviewsMap, setReviewsMap] = useState({});

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    // Check which orders can be reviewed and fetch existing reviews
    orders.forEach(order => {
      if (order.status === 'delivered') {
        checkCanReview(order._id);
        fetchReview(order._id);
      }
    });
  }, [orders]);

  const checkCanReview = async (orderId) => {
    try {
      const response = await axios.get(`${API_URL}/reviews/order/${orderId}/can-review`);
      setCanReviewMap(prev => ({ ...prev, [orderId]: response.data.canReview }));
    } catch (error) {
      console.error('Error checking review status:', error);
      setCanReviewMap(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const fetchReview = async (orderId) => {
    try {
      const response = await axios.get(`${API_URL}/reviews/order/${orderId}`);
      setReviewsMap(prev => ({ ...prev, [orderId]: response.data }));
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching review:', error);
      }
      // 404 is expected if no review exists, so we don't set anything
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/orders/me`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return <div className="orders-container"><div className="container">Loading...</div></div>;
  }

  return (
    <div className="orders-container">
      <div className="container">
        <h1>{t('nav.orders')}</h1>

        {orders.length === 0 ? (
          <div className="no-orders">{t('orders.noOrders') || 'No orders found'}</div>
        ) : (
          <div className="orders-list">
            {orders.map((order) => (
              <div key={order._id} className="order-card">
                <div className="order-header">
                  <div>
                    <h2>{order.restaurantName}</h2>
                    <p className="order-number">{t('orders.orderNumber') || 'Order #'}: {order.orderNumber}</p>
                    <p className="order-date">{formatDate(order.createdAt)}</p>
                  </div>
                  <div className="order-status">
                    <span className={`status-badge status-${order.status}`}>
                      {t(`activeOrder.status.${order.status}`) || order.status}
                    </span>
                  </div>
                </div>

                {order.status === 'cancelled' && order.cancellationMessage && (
                  <div className="order-cancellation-message">
                    <strong>{t('orders.cancellationReason') || 'Cancellation reason'}:</strong> {order.cancellationMessage}
                  </div>
                )}

                <div className="order-items">
                  {order.items.map((item, index) => (
                    <div key={index} className="order-item">
                      <div className="order-item-details">
                        <div className="order-item-header">
                          <span className="item-quantity">{item.quantity}x</span>
                          <span className="item-name">{item.name}</span>
                          <span className="item-price">€{(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                        {item.variationOptionPrices && item.variationOptionPrices.length > 0 ? (
                          <div className="order-item-variations">
                            {item.variationOptionPrices.map((v, vIdx) => (
                              <div key={vIdx} className="order-variation-group">
                                <span className="order-variation-name">{v.variationName}:</span>
                                <div className="order-variation-options">
                                  <span className="order-variation-badge">
                                    {v.optionName}{v.price != null && v.price !== 0 ? ` (+€${Number(v.price).toFixed(2)})` : ''}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : item.selectedVariations && (
                          <div className="order-item-variations">
                            {Array.isArray(item.selectedVariations) ? (
                              item.selectedVariations.map((variation, vIdx) => (
                                <div key={vIdx} className="order-variation-group">
                                  <span className="order-variation-name">{variation.name}:</span>
                                  <div className="order-variation-options">
                                    {variation.selectedOptions?.map((option, oIdx) => (
                                      <span key={oIdx} className="order-variation-badge">{option}</span>
                                    ))}
                                  </div>
                                </div>
                              ))
                            ) : (
                              Object.entries(item.selectedVariations).map(([variationName, selectedOptions], vIdx) => (
                                <div key={vIdx} className="order-variation-group">
                                  <span className="order-variation-name">{variationName}:</span>
                                  <div className="order-variation-options">
                                    {Array.isArray(selectedOptions) ? (
                                      selectedOptions.map((option, oIdx) => (
                                        <span key={oIdx} className="order-variation-badge">{option}</span>
                                      ))
                                    ) : (
                                      <span className="order-variation-badge">{selectedOptions}</span>
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="order-totals">
                  {order.subtotalBeforeTax !== undefined && order.subtotalBeforeTax !== null && (
                    <div className="total-row">
                      <span>{t('orders.subtotalBeforeTax') || 'Subtotal (before tax)'}:</span>
                      <span>€{order.subtotalBeforeTax.toFixed(2)}</span>
                    </div>
                  )}
                  {order.taxAmount !== undefined && order.taxAmount !== null && order.taxAmount > 0 && (
                    <div className="total-row">
                      <span>{t('orders.tax') || 'Tax'} {order.taxRate ? `(${order.taxRate.toFixed(1)}%)` : ''}:</span>
                      <span>€{order.taxAmount.toFixed(2)}</span>
                    </div>
                  )}
                  {order.deliveryFee > 0 && (
                    <div className="total-row">
                      <span>{t('orders.deliveryFee') || 'Delivery Fee'}:</span>
                      <span>€{order.deliveryFee.toFixed(2)}</span>
                    </div>
                  )}
                  {order.serviceFee !== undefined && order.serviceFee !== null && order.serviceFee > 0 && (
                    <div className="total-row">
                      <span>{t('orders.serviceFee') || 'Service Fee'} {order.serviceFeeRate ? `(${order.serviceFeeRate.toFixed(2)}%)` : ''}:</span>
                      <span>€{order.serviceFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="total-row">
                    <span>{t('orders.subtotal') || 'Subtotal'} {order.deliveryFee > 0 || (order.serviceFee > 0) ? (t('orders.includesFees') || '(includes fees)') : ''}:</span>
                    <span>€{order.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="total-row total">
                    <span>{t('orders.total') || 'Total'}:</span>
                    <span>€{((order.total || 0) + (order.walletAmount || 0)).toFixed(2)}</span>
                  </div>
                </div>

                <div className="payment-info">
                  <div className="payment-status">
                    <strong>{t('orders.paymentStatus') || 'Payment Status'}:</strong>
                    <span className={`payment-status-badge payment-${order.paymentStatus || 'pending'}`}>
                      {order.paymentStatus === 'paid' ? (t('orders.paid') || 'Paid') :
                       order.paymentStatus === 'pending' ? (t('orders.pending') || 'Pending') :
                       order.paymentStatus === 'failed' ? (t('orders.failed') || 'Failed') :
                       order.paymentStatus === 'refunded' ? (t('orders.refunded') || 'Refunded') :
                       (t('orders.pending') || 'Pending')}
                    </span>
                  </div>
                  <div className="payment-method">
                    <strong>{t('orders.paymentMethod') || 'Payment Method'}:</strong>
                    <span>
                      {order.paymentMethod === 'stripe' ? (t('orders.cardPayment') || 'Card Payment') :
                       order.paymentMethod === 'cash' ? (t('orders.cash') || 'Cash on Delivery') :
                       (t('orders.other') || 'Other')}
                    </span>
                  </div>
                </div>

                {order.deliveryAddress && (
                  <div className="delivery-info">
                    <strong>{t('orders.deliveryAddress') || 'Delivery Address'}:</strong>
                    <p>{order.deliveryAddress.street}, {order.deliveryAddress.city} {order.deliveryAddress.zipCode}{order.deliveryAddress.addressDetails ? ` — ${order.deliveryAddress.addressDetails}` : ''}</p>
                  </div>
                )}

                {/* Review Information */}
                {reviewsMap[order._id] && (
                  <div className="review-info">
                    <strong>{t('reviews.title') || 'Review'}:</strong>
                    <div className="review-details">
                      {reviewsMap[order._id].deliveryRating && (
                        <div className="review-rating-item">
                          <span className="review-label">{t('reviews.deliveryRating') || 'Delivery Rating'}:</span>
                          <div className="review-stars-display">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <span key={star} className={star <= reviewsMap[order._id].deliveryRating ? 'filled' : ''}>★</span>
                            ))}
                            <span className="rating-value">({reviewsMap[order._id].deliveryRating})</span>
                          </div>
                          {reviewsMap[order._id].deliveryComment && (
                            <p className="review-comment-text">{reviewsMap[order._id].deliveryComment}</p>
                          )}
                        </div>
                      )}
                      {reviewsMap[order._id].restaurantRating && (
                        <div className="review-rating-item">
                          <span className="review-label">
                            {order.restaurantType === 'Store' 
                              ? (t('reviews.storeRating') || 'Store Rating')
                              : (t('reviews.restaurantRating') || 'Restaurant Rating')}:
                          </span>
                          <div className="review-stars-display">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <span key={star} className={star <= reviewsMap[order._id].restaurantRating ? 'filled' : ''}>★</span>
                            ))}
                            <span className="rating-value">({reviewsMap[order._id].restaurantRating})</span>
                          </div>
                          {reviewsMap[order._id].restaurantComment && (
                            <p className="review-comment-text">{reviewsMap[order._id].restaurantComment}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setSelectedOrder(selectedOrder === order._id ? null : order._id)}
                    className="btn btn-primary btn-sm"
                  >
                    {selectedOrder === order._id ? (t('orders.hideReceipt') || 'Hide Receipt') : (t('orders.viewReceipt') || 'View Receipt')}
                  </button>
                  <button
                    onClick={() => setMessagesOrder(order)}
                    className="btn btn-secondary btn-sm"
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <i className="fas fa-comments"></i>
                    {t('orders.messages') || 'Chat'}
                  </button>
                  {order.status === 'delivered' && canReviewMap[order._id] && (
                    <button
                      onClick={() => setReviewOrder(order)}
                      className="btn btn-primary btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <i className="fas fa-star"></i>
                      {t('review.button')}
                    </button>
                  )}
                </div>

                {selectedOrder === order._id && (
                  <div className="receipt">
                    <div className="receipt-header">
                      <h3>{t('orders.receipt') || 'Receipt'}</h3>
                      <p>{order.restaurantName}</p>
                      <p>{formatDate(order.createdAt)}</p>
                      <p>{t('orders.orderNumber') || 'Order #'}: {order.orderNumber}</p>
                    </div>
                    <div className="receipt-items">
                      {order.items.map((item, index) => (
                        <div key={index} className="receipt-item">
                          <div>
                            <span className="receipt-quantity">{item.quantity}x</span>
                            <span className="receipt-name">{item.name}</span>
                            {item.variationOptionPrices && item.variationOptionPrices.length > 0 ? (
                              <div className="receipt-variations">
                                {item.variationOptionPrices.map((v, vIdx) => (
                                  <div key={vIdx} className="receipt-variation-group">
                                    <span className="receipt-variation-name">{v.variationName}:</span>
                                    <div className="receipt-variation-options">
                                      <span className="receipt-variation-badge">
                                        {v.optionName}{v.price != null && v.price !== 0 ? ` (+€${Number(v.price).toFixed(2)})` : ''}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : item.selectedVariations && (
                              <div className="receipt-variations">
                                {Array.isArray(item.selectedVariations) ? (
                                  item.selectedVariations.map((variation, vIdx) => (
                                    <div key={vIdx} className="receipt-variation-group">
                                      <span className="receipt-variation-name">{variation.name}:</span>
                                      <div className="receipt-variation-options">
                                        {variation.selectedOptions?.map((option, oIdx) => (
                                          <span key={oIdx} className="receipt-variation-badge">{option}</span>
                                        ))}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  Object.entries(item.selectedVariations).map(([variationName, selectedOptions], vIdx) => (
                                    <div key={vIdx} className="receipt-variation-group">
                                      <span className="receipt-variation-name">{variationName}:</span>
                                      <div className="receipt-variation-options">
                                        {Array.isArray(selectedOptions) ? (
                                          selectedOptions.map((option, oIdx) => (
                                            <span key={oIdx} className="receipt-variation-badge">{option}</span>
                                          ))
                                        ) : (
                                          <span className="receipt-variation-badge">{selectedOptions}</span>
                                        )}
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                          <span className="receipt-price">€{(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="receipt-totals">
                      {order.subtotalBeforeTax !== undefined && order.subtotalBeforeTax !== null && (
                        <div className="receipt-total-row">
                          <span>{t('orders.subtotalBeforeTax') || 'Subtotal (before tax)'}:</span>
                          <span>€{order.subtotalBeforeTax.toFixed(2)}</span>
                        </div>
                      )}
                      {order.taxAmount !== undefined && order.taxAmount !== null && order.taxAmount > 0 && (
                        <div className="receipt-total-row">
                          <span>{t('orders.tax') || 'Tax'} {order.taxRate ? `(${order.taxRate.toFixed(1)}%)` : ''}:</span>
                          <span>€{order.taxAmount.toFixed(2)}</span>
                        </div>
                      )}
                      {order.deliveryFee > 0 && (
                        <div className="receipt-total-row">
                          <span>{t('orders.deliveryFee') || 'Delivery Fee'}:</span>
                          <span>€{order.deliveryFee.toFixed(2)}</span>
                        </div>
                      )}
                      {order.serviceFee !== undefined && order.serviceFee !== null && order.serviceFee > 0 && (
                        <div className="receipt-total-row">
                          <span>{t('orders.serviceFee') || 'Service Fee'} {order.serviceFeeRate ? `(${order.serviceFeeRate.toFixed(2)}%)` : ''}:</span>
                          <span>€{order.serviceFee.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="receipt-total-row">
                        <span>{t('orders.subtotal') || 'Subtotal'} {order.deliveryFee > 0 || (order.serviceFee > 0) ? (t('orders.includesFees') || '(includes fees)') : ''}:</span>
                        <span>€{order.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="receipt-total-row receipt-total">
                        <span>{t('orders.total') || 'Total'}:</span>
                        <span>€{((order.total || 0) + (order.walletAmount || 0)).toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="receipt-payment-info">
                      <div className="receipt-payment-row">
                        <span>{t('orders.paymentStatus') || 'Payment Status'}:</span>
                        <span className={`payment-status-badge payment-${order.paymentStatus || 'pending'}`}>
                          {order.paymentStatus === 'paid' ? (t('orders.paid') || 'Paid') :
                           order.paymentStatus === 'pending' ? (t('orders.pending') || 'Pending') :
                           order.paymentStatus === 'failed' ? (t('orders.failed') || 'Failed') :
                           order.paymentStatus === 'refunded' ? (t('orders.refunded') || 'Refunded') :
                           (t('orders.pending') || 'Pending')}
                        </span>
                      </div>
                      <div className="receipt-payment-row">
                        <span>{t('orders.paymentMethod') || 'Payment Method'}:</span>
                        <span>
                          {order.paymentMethod === 'stripe' ? (t('orders.cardPayment') || 'Card Payment') :
                           order.paymentMethod === 'cash' ? (t('orders.cash') || 'Cash on Delivery') :
                           (t('orders.other') || 'Other')}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {messagesOrder && (
        <OrderMessages order={messagesOrder} onClose={() => setMessagesOrder(null)} />
      )}

      {reviewOrder && (
        <ReviewModal
          order={reviewOrder}
          onClose={() => {
            setReviewOrder(null);
            checkCanReview(reviewOrder._id);
          }}
          onReviewSubmitted={() => {
            setCanReviewMap(prev => ({ ...prev, [reviewOrder._id]: false }));
            fetchReview(reviewOrder._id);
          }}
        />
      )}
    </div>
  );
};

export default Orders;
